import { useMemo } from 'react';
import config from '@cardash/config';
import { Spinner } from 'reactstrap';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import styled from 'styled-components';
import useMainContext from '../../hooks/main-provider';
import { checkRouteAuthorization, getRouteByPath } from '../../utils/auth';
import MenuSidebar from '../Buttons/MenuSidebar';
import TopNavBar from '../TopNavBar';
import { useSidebar } from '../../contexts/SidebarContext';

const SpinnerContainer = styled.div`
  display: flex;
  height: 100vh;
  justify-content: center;
  align-items: center;
`;

const LayoutContainer = styled.div`
  min-height: 100vh;
`;

const MenuSideBarWrapper = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  z-index: 1000;
  height: 100vh;
`;

const TopNavBarWrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  left: ${props => props.sidebarWidth}px;
  width: calc(100% - ${props => props.sidebarWidth}px);
  z-index: 999;
  transition: all 0.3s ease;
`;

const MainContentContainer = styled.div`
  display: flex;
  min-height: 100vh;
`;

const MainContentArea = styled.div`
  margin-left: ${props => (props.hasUser ? `${props.sidebarWidth}px` : 0)};
  width: ${props => (props.hasUser ? `calc(100% - ${props.sidebarWidth}px)` : '100%')};
  transition: all 0.3s ease;
`;

const ContentWrapper = styled.div`
  margin-top: 55px;
  transition: all 0.3s ease;
`;

const InnerContentWrapper = styled.div`
  width: 100%;
  padding: ${props => (props.hasUser ? undefined : 0)};
`;

const ContentContainer = styled.div`
  max-width: ${props => (props.fullWidth ? '100%' : '1500px')};
`;

const Layout = ({ children }) => {
  const { pathname } = useRouter();
  const { viewMode } = useMainContext();
  const { data: session, status } = useSession();
  const { sidebarWidth } = useSidebar();
  const { routes } = config;
  const user = session?.user;

  const sideBarPages = useMemo(
    () =>
      Object.values(routes || {}).filter(
        route =>
          route.pageName &&
          checkRouteAuthorization(route.path, user?.type, viewMode) &&
          route.navBarItemInViewModes.findIndex(mode => mode === (viewMode || config.viewModes.Master)) !== -1
      ),
    [routes, user, viewMode]
  );

  const route = getRouteByPath(routes, pathname);

  if (status === 'loading') {
    return (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    );
  }

  return (
    <LayoutContainer>
      {user && (
        <>
          <MenuSideBarWrapper>
            <MenuSidebar pages={sideBarPages} selectedPageName={route?.pageName} />
          </MenuSideBarWrapper>
          <TopNavBarWrapper sidebarWidth={sidebarWidth}>
            <TopNavBar />
          </TopNavBarWrapper>
        </>
      )}
      <MainContentContainer>
        <MainContentArea hasUser={!!user} sidebarWidth={sidebarWidth}>
          <ContentWrapper>
            <InnerContentWrapper className="d-flex flex-column flex-fill align-items-center" hasUser={!!user}>
              <ContentContainer
                className={`d-flex justify-content-center w-100 flex-fill ${route?.fullWidth ? 'px-2' : 'px-5'}`}
                fullWidth={route?.fullWidth}
              >
                {children}
              </ContentContainer>
            </InnerContentWrapper>
          </ContentWrapper>
        </MainContentArea>
      </MainContentContainer>
    </LayoutContainer>
  );
};

export default Layout;
